<script setup lang="ts">
import {onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {EditorView, basicSetup} from 'codemirror';
import {EditorState} from '@codemirror/state';
import {xml} from '@codemirror/lang-xml';

// Zweiweg-Bindung des rohen MusicXML-Quelltexts (VUE_RULES.md: State-Sync nur via defineModel).
const value = defineModel<string>('value', {required: true, default: ''});

const hostRef = ref<HTMLElement | null>(null);
let view: EditorView | null = null;

onMounted(() => {
  if (!hostRef.value) return;

  view = new EditorView({
    parent: hostRef.value,
    state: EditorState.create({

      doc: value.value ?? '',
      extensions: [
        basicSetup,
        xml(),
        EditorView.lineWrapping,
        // Beim Tippen den Modelwert aktualisieren (löst das Live-Rendering im Parent aus).
        EditorView.updateListener.of((update) => {
          if (!update.docChanged) return;
          const doc = update.state.doc.toString();
          if (doc !== value.value) value.value = doc;
        }),
      ],
    }),
  });
});

// Externe Wertänderung (Datei-Laden, Undo/Redo, Note-/Akkord-Edit) einspielen –
// nur wenn sie sich vom aktuellen Editor-Inhalt unterscheidet (verhindert Cursor-Reset/Schleife).
watch(value, (newValue) => {
  if (!view) return;
  const current = view.state.doc.toString();
  if ((newValue ?? '') === current) return;
  view.dispatch({
    changes: {from: 0, to: current.length, insert: newValue ?? ''},
  });
});

onBeforeUnmount(() => {
  view?.destroy();
  view = null;
});
</script>

<template>
  <div
      ref="hostRef"
      class="h-full w-full overflow-auto rounded-xl border border-gray-200 bg-white font-mono text-xs shadow-inner"
  />
</template>
